import { AnalyticsEvent } from 'services/analytics'
import { CanonicalLoad, canonicalLoadboardAnalyticsName } from '@numeo/types-core'
import type { LoadHubSearchTrigger } from '@numeo/types'
import { LoadFiltersState, LocationData } from '../types/filters'
import { formatDateYMD } from './date'
import { deduplicateLoads, filterLoads } from './filters'
import { createLoadHubAnalyticsId, LoadHubAnalyticsProperties, trackLoadHubEvent } from './analyticsContext'

export type SearchTrigger = LoadHubSearchTrigger

type LegacySearchTrigger = 'manual' | 'recent' | 'auto_refresh'
type SourceSearchOutcome = 'results' | 'empty' | 'auth_required' | 'timeout' | 'error' | 'cancelled' | 'skipped'
type SourceSkipReason =
    | 'unsupported_equipment'
    | 'not_connected'
    | 'extension_missing'
    | 'already_in_flight'
    | 'no_prior_results'
    | 'not_selected'
    | 'other'
    | 'not_applicable'

const SOURCE_SEARCH_SETTLEMENT_TIMEOUT_MS = 2 * 60 * 1000

export interface SearchCorrelationContext {
    searchCommandId: string
    searchId: string
    searchExecutionId: string
}

interface SearchSubmissionOptions {
    filters?: LoadFiltersState
    searchId?: string
    refreshAverageSeconds?: number
    autoRefreshEnabled?: boolean
    searchOrigin?: 'search_button' | 'recent_search'
    filterOrigin?: 'default' | 'user_edit' | 'recent_search' | 'restored' | 'promo_prefill'
}

interface SourceTerminalState {
    resultCount: number
    status: 'succeeded' | 'failed' | 'skipped' | 'cancelled'
    outcome: SourceSearchOutcome
}

const uniqueBoards = (loadboards: string[]): string[] => [...new Set(loadboards.map(canonicalLoadboardAnalyticsName))]

const parseNumber = (value: string | undefined): number | undefined => {
    if (!value?.trim()) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

const parseHours = (value: string | undefined): number | undefined => {
    const match = value?.trim().match(/^(\d+)(?:h)?$/i)
    if (!match) return undefined
    const parsed = Number(match[1])
    return Number.isFinite(parsed) ? parsed : undefined
}

const locationValues = (options: LocationData[] | undefined, fallback: LocationData | null): LocationData[] =>
    options?.length ? options : fallback ? [fallback] : []

const locationSnapshot = (prefix: 'origin' | 'destination', values: LocationData[]): LoadHubAnalyticsProperties => {
    if (values.length === 0) return { [`${prefix}_kind`]: prefix === 'destination' ? 'anywhere' : 'unknown', [`${prefix}_location_count`]: 0 }
    const zones = values.flatMap((value) => (value.zoneCode ? [value.zoneCode] : []))
    const states = values.flatMap((value) => (value.stateProv ? [value.stateProv.toUpperCase()] : []))
    const cityCount = values.filter((value) => Boolean(value.city)).length
    const kind = zones.length > 0 ? 'zone' : cityCount > 0 ? 'city' : 'state'
    return {
        [`${prefix}_kind`]: kind,
        [`${prefix}_location_count`]: values.length,
        ...(zones.length ? { [`${prefix}_zone_codes`]: [...new Set(zones)] } : {}),
        ...(states.length ? { [`${prefix}_state_codes`]: [...new Set(states)] } : {}),
    }
}

const pickupWindowDays = (startDate: Date | null, endDate: Date | null): number | undefined => {
    if (!startDate || !endDate) return undefined
    return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1)
}

/** A bounded, chartable snapshot. It intentionally excludes city names, coordinates and broker display names. */
const buildLoadHubFilterSnapshot = (
    filters: LoadFiltersState,
    filterOrigin: SearchSubmissionOptions['filterOrigin'] = 'user_edit'
): LoadHubAnalyticsProperties => {
    const origin = locationValues(filters.originOptions, filters.origin)
    const destination = locationValues(filters.destinationOptions, filters.destination)
    const activeFields = [
        origin.length ? 'origin' : null,
        destination.length ? 'destination' : null,
        filters.equipmentTypes.length ? 'equipment_types' : null,
        filters.originDhd ? 'origin_deadhead_miles' : null,
        filters.destinationDhd ? 'destination_deadhead_miles' : null,
        filters.lengthFt ? 'max_length_ft' : null,
        filters.weightLbs ? 'max_weight_lbs' : null,
        filters.minWeight ? 'min_weight_lbs' : null,
        filters.minR ? 'min_rate_usd' : null,
        filters.minT ? 'min_rpm_usd' : null,
        filters.minMi ? 'min_trip_miles' : null,
        filters.maxMi ? 'max_trip_miles' : null,
        filters.searchBack ? 'max_load_age_hours' : null,
        filters.companies.length ? 'broker_companies' : null,
    ].filter((field): field is string => field !== null)

    const loadType = filters.fullPartial === 'FULL' ? 'full' : filters.fullPartial === 'PARTIAL' ? 'partial' : 'full_and_partial'
    return {
        filters_version: 1,
        equipment_dictionary_version: 1,
        equipment_types: [...filters.equipmentTypes],
        filter_origin: filterOrigin ?? 'user_edit',
        active_filter_fields: activeFields,
        changed_from_default_fields: activeFields.filter(
            (field) => !['origin', 'equipment_types', 'origin_deadhead_miles', 'destination_deadhead_miles'].includes(field)
        ),
        ...locationSnapshot('origin', origin),
        ...locationSnapshot('destination', destination),
        ...(parseNumber(filters.originDhd) !== undefined ? { origin_deadhead_miles: parseNumber(filters.originDhd) } : {}),
        ...(parseNumber(filters.destinationDhd) !== undefined ? { destination_deadhead_miles: parseNumber(filters.destinationDhd) } : {}),
        ...(filters.startDate ? { pickup_start_date: formatDateYMD(filters.startDate) } : {}),
        ...(filters.endDate ? { pickup_end_date: formatDateYMD(filters.endDate) } : {}),
        ...(pickupWindowDays(filters.startDate, filters.endDate) !== undefined
            ? { pickup_window_days: pickupWindowDays(filters.startDate, filters.endDate) }
            : {}),
        load_type: loadType,
        ...(parseNumber(filters.lengthFt) !== undefined ? { max_length_ft: parseNumber(filters.lengthFt) } : {}),
        ...(parseNumber(filters.weightLbs) !== undefined ? { max_weight_lbs: parseNumber(filters.weightLbs) } : {}),
        ...(parseNumber(filters.minWeight) !== undefined ? { min_weight_lbs: parseNumber(filters.minWeight) } : {}),
        ...(parseNumber(filters.minR) !== undefined ? { min_rate_usd: parseNumber(filters.minR) } : {}),
        ...(parseNumber(filters.minT) !== undefined ? { min_rpm_usd: parseNumber(filters.minT) } : {}),
        ...(parseNumber(filters.minMi) !== undefined ? { min_trip_miles: parseNumber(filters.minMi) } : {}),
        ...(parseNumber(filters.maxMi) !== undefined ? { max_trip_miles: parseNumber(filters.maxMi) } : {}),
        ...(parseHours(filters.searchBack) !== undefined ? { max_load_age_hours: parseHours(filters.searchBack) } : {}),
        company_filter_count: filters.companies.length,
    }
}

const normalizeTrigger = (trigger: SearchTrigger | LegacySearchTrigger): SearchTrigger => {
    if (trigger === 'manual') return 'manual_search'
    if (trigger === 'recent') return 'manual_search'
    return trigger
}

export const trackSearchSubmitted = (
    triggerValue: SearchTrigger | LegacySearchTrigger,
    loadboards: string[],
    options: SearchSubmissionOptions = {}
): SearchCorrelationContext => {
    const trigger = normalizeTrigger(triggerValue)
    const boards = uniqueBoards(loadboards)
    const context: SearchCorrelationContext = {
        searchCommandId: createLoadHubAnalyticsId('scmd'),
        searchId: options.searchId ?? createLoadHubAnalyticsId('search'),
        searchExecutionId: createLoadHubAnalyticsId('exec'),
    }
    const snapshot = options.filters ? buildLoadHubFilterSnapshot(options.filters, options.filterOrigin) : {}
    const humanActivity = trigger === 'manual_search' || trigger === 'manual_refresh'
    const common = {
        search_command_id: context.searchCommandId,
        search_id: context.searchId,
        search_execution_id: context.searchExecutionId,
        trigger,
        selected_sources: boards,
        source_count: boards.length,
        auto_refresh_enabled: options.autoRefreshEnabled ?? false,
        ...(options.refreshAverageSeconds !== undefined ? { refresh_average_seconds: options.refreshAverageSeconds } : {}),
        ...snapshot,
    }

    // E14 is one accepted user search. Automatic/manual refreshes are executions of
    // the existing search and must not inflate the manual-search denominator.
    if (trigger === 'manual_search') {
        trackLoadHubEvent(
            AnalyticsEvent.LOAD_HUB_SEARCH_CLICKED,
            {
                ...common,
                search_origin: options.searchOrigin ?? (triggerValue === 'recent' ? 'recent_search' : 'search_button'),
                // Temporary compatibility for the existing daily-stats report. Identity and
                // company attribution come from the unified analytics context, never display fields.
                sources: boards.join(','),
            },
            { humanActivity }
        )
    }

    // Retain the first-generation event during migration; new dashboards use E14/E16.
    trackLoadHubEvent(
        AnalyticsEvent.LOAD_HUB_SEARCH_SUBMITTED,
        {
            trigger,
            loadboards: boards,
            selected_loadboard_count: boards.length,
            ...contextProperties(context),
        },
        { humanActivity }
    )
    return context
}

export const trackSearchBlocked = (params: {
    trigger: SearchTrigger | LegacySearchTrigger
    blockingReason: 'validation' | 'no_source' | 'auth' | 'extension' | 'quota' | 'entitlement' | 'already_in_flight'
    invalidFields?: string[]
    loadboards?: string[]
    quotaRemaining?: number
}): void => {
    const trigger = normalizeTrigger(params.trigger)
    trackLoadHubEvent(
        AnalyticsEvent.WEB_LOAD_HUB_SEARCH_BLOCKED,
        {
            search_command_id: createLoadHubAnalyticsId('scmd'),
            trigger,
            blocking_reason: params.blockingReason,
            invalid_fields: params.invalidFields ?? [],
            selected_sources: uniqueBoards(params.loadboards ?? []),
            ...(params.quotaRemaining !== undefined ? { quota_remaining: params.quotaRemaining } : {}),
        },
        { humanActivity: trigger === 'manual_search' || trigger === 'manual_refresh' }
    )
}

const contextProperties = (context: SearchCorrelationContext): LoadHubAnalyticsProperties => ({
    search_command_id: context.searchCommandId,
    search_id: context.searchId,
    search_execution_id: context.searchExecutionId,
})

export interface SourceSearchTracker {
    context: SearchCorrelationContext
    sourceRequests: Array<{ loadboard: string; sourceRequestId: string }>
    recordResults: (board: string, loads: CanonicalLoad[]) => void
    complete: (board: string, outcome: SourceSearchOutcome, resultCount?: number, skipReason?: SourceSkipReason) => void
    completeBoards: (boards: string[], outcome: SourceSearchOutcome, skipReason?: SourceSkipReason) => void
}

export const trackExtensionSourceSearch = (
    tracker: SourceSearchTracker,
    phase: 'started' | 'completed',
    details: {
        startedAt: number
        outcome?: 'success' | 'failed' | 'timeout' | 'cancelled'
        providerResultCount?: number
        normalizedResultCount?: number
        errorCode?: string
        providerEquipmentCodes?: string[]
        equipmentMappingStatus?: 'fully_supported' | 'partially_supported' | 'unsupported' | 'unknown'
    }
): void => {
    const source = tracker.sourceRequests.find((item) => item.loadboard === canonicalLoadboardAnalyticsName('DAT_EXTENSION'))
    if (!source) return
    const common = {
        ...contextProperties(tracker.context),
        source_request_id: source.sourceRequestId,
        loadboard: source.loadboard,
        requested_source_group: source.loadboard,
        transport: 'extension',
        attribution_status: 'linked',
    }
    if (phase === 'started') {
        trackLoadHubEvent(
            AnalyticsEvent.EXT_LOAD_HUB_SOURCE_SEARCH_STARTED,
            { ...common, request_started_at: new Date(details.startedAt).toISOString() },
            {
                humanActivity: false,
                initiatedFrom: 'extension',
                platform: 'extension',
            }
        )
        return
    }
    trackLoadHubEvent(
        AnalyticsEvent.EXT_LOAD_HUB_SOURCE_SEARCH_COMPLETED,
        {
            ...common,
            outcome: details.outcome ?? 'failed',
            provider_result_count: Math.max(0, details.providerResultCount ?? 0),
            normalized_result_count: Math.max(0, details.normalizedResultCount ?? 0),
            response_latency_ms: Math.max(0, Date.now() - details.startedAt),
            provider_equipment_codes: details.providerEquipmentCodes ?? [],
            equipment_mapping_status: details.equipmentMappingStatus ?? 'unknown',
            request_retry_count: 0,
            result_origin: 'live_provider',
            cache_age_available: false,
            ...(details.errorCode
                ? {
                      error_id: createLoadHubAnalyticsId('err'),
                      error_category: details.outcome === 'timeout' ? 'network' : 'provider',
                      error_code: details.errorCode,
                      failure_stage: 'provider_search',
                      is_retriable: true,
                      user_visible: true,
                  }
                : {}),
        },
        { humanActivity: false, initiatedFrom: 'extension', platform: 'extension' }
    )
}

export const createSourceSearchTracker = (
    loadboards: string[],
    triggerValue: SearchTrigger | LegacySearchTrigger,
    context?: SearchCorrelationContext,
    options: {
        filters?: LoadFiltersState
        initialLoads?: CanonicalLoad[]
        refreshAverageSeconds?: number
        scheduledDelayMs?: number
        actualGapMs?: number
        onSettled?: (newUniqueCount: number, outcome: 'success' | 'partial_success' | 'failed' | 'skipped') => void
    } = {}
): SourceSearchTracker => {
    const trigger = normalizeTrigger(triggerValue)
    const startedAt = Date.now()
    const boards = uniqueBoards(loadboards)
    const correlation =
        context ??
        ({
            searchCommandId: createLoadHubAnalyticsId('scmd'),
            searchId: createLoadHubAnalyticsId('search'),
            searchExecutionId: createLoadHubAnalyticsId('exec'),
        } satisfies SearchCorrelationContext)
    const pending = new Set(boards)
    const sourceRequestIds = new Map(boards.map((board) => [board, createLoadHubAnalyticsId('src_req')]))
    const terminal = new Map<string, SourceTerminalState>()
    const resultLoads = new Map<string, CanonicalLoad[]>()
    let firstResultAt: number | undefined
    let settled = false

    trackLoadHubEvent(
        AnalyticsEvent.WEB_LOAD_HUB_SEARCH_EXECUTION_STARTED,
        {
            ...contextProperties(correlation),
            trigger,
            requested_sources: boards,
            filters_version: 1,
            dispatch_at: new Date(startedAt).toISOString(),
            ...(options.refreshAverageSeconds !== undefined ? { refresh_average_seconds: options.refreshAverageSeconds } : {}),
            ...(options.scheduledDelayMs !== undefined ? { scheduled_delay_ms: options.scheduledDelayMs } : {}),
            ...(options.actualGapMs !== undefined ? { actual_gap_ms: options.actualGapMs } : {}),
        },
        { humanActivity: trigger === 'manual_search' || trigger === 'manual_refresh' }
    )

    const settleExecution = (): void => {
        if (settled || pending.size > 0) return
        settled = true
        clearTimeout(settlementTimeout)
        const succeeded = boards.filter((board) => terminal.get(board)?.status === 'succeeded')
        const failed = boards.filter((board) => terminal.get(board)?.status === 'failed')
        const skipped = boards.filter((board) => terminal.get(board)?.status === 'skipped')
        const cancelled = boards.filter((board) => terminal.get(board)?.status === 'cancelled')
        const fetchedCount = [...terminal.values()].reduce((sum, result) => sum + result.resultCount, 0)
        const executionLoads = deduplicateLoads([...resultLoads.values()].flat())
        const initialLoads = deduplicateLoads(options.initialLoads ?? [])
        const mergedLoads = deduplicateLoads([...initialLoads, ...executionLoads])
        const visibleCount = options.filters ? filterLoads(mergedLoads, options.filters).length : mergedLoads.length
        const outcome: 'success' | 'partial_success' | 'failed' | 'timeout' | 'cancelled' | 'skipped' =
            succeeded.length > 0 && failed.length === 0 && skipped.length === 0
                ? 'success'
                : succeeded.length > 0
                  ? 'partial_success'
                  : cancelled.length === boards.length
                    ? 'cancelled'
                    : failed.length > 0 && failed.every((board) => terminal.get(board)?.outcome === 'timeout')
                      ? 'timeout'
                      : failed.length > 0
                        ? 'failed'
                        : 'skipped'
        const newUniqueCount = Math.max(0, mergedLoads.length - initialLoads.length)
        trackLoadHubEvent(
            AnalyticsEvent.WEB_LOAD_HUB_SEARCH_EXECUTION_COMPLETED,
            {
                ...contextProperties(correlation),
                trigger,
                outcome,
                requested_sources: boards,
                succeeded_sources: succeeded,
                failed_sources: failed,
                skipped_sources: [...skipped, ...cancelled],
                fetched_count: fetchedCount,
                had_results: fetchedCount > 0,
                settlement_duration_ms: Math.max(0, Date.now() - startedAt),
                ...(firstResultAt !== undefined ? { time_to_first_result_ms: firstResultAt - startedAt } : {}),
                visible_count: visibleCount,
                deduplicated_count: executionLoads.length,
                new_unique_count: newUniqueCount,
            },
            { humanActivity: false }
        )
        options.onSettled?.(newUniqueCount, outcome === 'cancelled' || outcome === 'timeout' ? 'failed' : outcome)
        resultLoads.clear()
    }

    const complete = (boardValue: string, outcome: SourceSearchOutcome, resultCount: number = 0, skipReason?: SourceSkipReason): void => {
        if (settled) return
        const board = canonicalLoadboardAnalyticsName(boardValue)
        if (!pending.delete(board)) return
        if (resultCount > 0 && firstResultAt === undefined) firstResultAt = Date.now()
        const status: SourceTerminalState['status'] =
            outcome === 'results' || outcome === 'empty' ? 'succeeded' : outcome === 'skipped' ? 'skipped' : outcome === 'cancelled' ? 'cancelled' : 'failed'
        terminal.set(board, { resultCount: Math.max(0, resultCount), status, outcome })

        // First-generation source event retained for reconciliation. It intentionally
        // keeps the richer old outcomes while E17 owns the aggregate execution result.
        trackLoadHubEvent(
            AnalyticsEvent.LOAD_HUB_SOURCE_SEARCH_COMPLETED,
            {
                ...contextProperties(correlation),
                loadboard: board,
                source_request_id: sourceRequestIds.get(board),
                trigger,
                outcome,
                result_count: Math.max(0, resultCount),
                duration_ms: Math.max(0, Date.now() - startedAt),
                ...(skipReason ? { skip_reason: skipReason } : {}),
            },
            { humanActivity: false }
        )
        settleExecution()
    }

    const settlementTimeout = setTimeout(() => {
        ;[...pending].forEach((board) => complete(board, 'timeout'))
    }, SOURCE_SEARCH_SETTLEMENT_TIMEOUT_MS)
    if (boards.length === 0) settleExecution()

    return {
        context: correlation,
        sourceRequests: boards.map((loadboard) => ({ loadboard, sourceRequestId: sourceRequestIds.get(loadboard)! })),
        recordResults: (boardValue, loads) => {
            if (settled || loads.length === 0) return
            const board = canonicalLoadboardAnalyticsName(boardValue)
            resultLoads.set(board, [...(resultLoads.get(board) ?? []), ...loads])
            firstResultAt ??= Date.now()
        },
        complete,
        completeBoards: (values, outcome, skipReason) => values.forEach((board) => complete(board, outcome, 0, skipReason)),
    }
}

export const trackFiltersApplied = (params: {
    filters: LoadFiltersState
    changedFields: string[]
    filterScope: 'search' | 'results'
    context?: SearchCorrelationContext
    visibleCountBefore?: number
    visibleCountAfter?: number
    filterOrigin?: SearchSubmissionOptions['filterOrigin']
}): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_FILTERS_APPLIED, {
        ...(params.context ? contextProperties(params.context) : {}),
        filter_scope: params.filterScope,
        changed_fields: params.changedFields,
        ...buildLoadHubFilterSnapshot(params.filters, params.filterOrigin),
        ...(params.visibleCountBefore !== undefined ? { visible_count_before: params.visibleCountBefore } : {}),
        ...(params.visibleCountAfter !== undefined ? { visible_count_after: params.visibleCountAfter } : {}),
    })
}

const FILTER_FIELDS: Array<keyof LoadFiltersState> = [
    'origin',
    'destination',
    'originOptions',
    'destinationOptions',
    'originDhd',
    'destinationDhd',
    'equipmentTypes',
    'fullPartial',
    'startDate',
    'endDate',
    'lengthFt',
    'weightLbs',
    'minWeight',
    'minR',
    'minT',
    'minMi',
    'maxMi',
    'searchBack',
    'companies',
    'loadSources',
]

export const changedLoadHubFilterFields = (previous: LoadFiltersState | undefined, current: LoadFiltersState): string[] => {
    if (!previous) return FILTER_FIELDS.filter((field) => current[field] !== undefined).map(String)
    return FILTER_FIELDS.filter((field) => JSON.stringify(previous[field]) !== JSON.stringify(current[field])).map(String)
}
